/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useRef } from 'react'
import { FormGroup } from 'react-bootstrap'
import { Formik, yupToFormErrors, FormikProps, FieldArray } from 'formik'
import { DatePicker, TextArea } from '@mdsol/lego'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useServiceContext } from '../../../root'
import { Select } from '../../../components/Select/Select'
import Modal from '../../../components/Modal'

import DocTypeDropdown from '../../../components/doc-type-dropdown/DocTypeDropdown'
import { DocumentMigrationValidationSchema } from './validation'
import { FOLDER_TYPE_DOCUMENT_TYPES_ALLOWED, FolderName, SystemFolderType } from './constants'
import type { ApproverStep } from '../../../components/workflows/UserGroupSteps'
import { UserGroupSteps } from '../../../components/workflow/UserGroupSteps'
import FileDropZone from './FileDropZone'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import { useEsignature } from '../../../hooks/useEsignature'
import { useModal } from '../../../contexts/ModalContext'
import RcmEsignatureModal from '../../../components/esignature/RcmEsignatureModal'
import type {
  DocumentMigrationFormData,
  DocumentMigrationModalProps,
  FileWithMetadata,
  FolderTypeOption,
  DocumentTypeOption,
  WorkflowStepData
} from './types'
import type { MigrationParams } from '../../../services/api/filesMigration'
import { changeRequestTypesApiService } from '../../../services/api/api.changerequesttypes'
import { documentTypesApiService } from '../../../services/api/api.documenttypes'
import { getMomentDateFormat, getDatePickerParseFormats } from '~/utils/dateFormatting'
const { getById } = documentTypesApiService

export const DocumentMigrationModal: React.FC<DocumentMigrationModalProps> = ({
  isOpen: isOpenProp,
  onClose,
  onSuccess,
  folderId,
  folderUuid,
  isSiteUpload = false,
  externalAndUserSimpleMigration = false,
  linkedEntities: linkedEntitiesProp,
  canEditBlinded: canEditBlindedProp = false,
  params
}) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const queryClient = useQueryClient()
  const { showModal } = useModal()
  const formikRef = useRef<FormikProps<DocumentMigrationFormData> | null>(null)
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()

  const [selectedDocTypeObj, setSelectedDocTypeObj] = useState<DocumentTypeOption | null>(null)
  const [allowedDocTypes, setAllowedDocTypes] = useState<number[] | null>(null)
  const [linkedEntities, setLinkedEntities] = useState<any>(linkedEntitiesProp)
  const [availableFolderTypes, setAvailableFolderTypes] = useState<FolderTypeOption[] | null>(null)
  const [folderType, setFolderType] = useState<any>({})
  const [canEditBlinded, setCanEditBlinded] = useState(canEditBlindedProp)
  const [isFolderWithinMedidataClinicalLineage, setIsFolderWithinMedidataClinicalLineage] = useState(false)
  const validationSchema = DocumentMigrationValidationSchema(t)
  const textFactory = context.service.textsFactory()

  const [formData, setFormData] = useState<DocumentMigrationFormData>({
    files: [],
    documentTypeTemplateId: null,
    folderTypeTemplateId: null,
    selectedDocTypeObj: null,
    typeData: {
      approvers: { steps: [] as ApproverStep[] },
      reviewers: { steps: [] as ApproverStep[] },
      collaborators: { steps: [] as ApproverStep[] }
    },
    createCR: false,
    expirationDate: null,
    continueToEditPhase: false,
    combineImages: false,
    description: externalAndUserSimpleMigration
      ? t('CR_DESC_DEFAULT_PLACEHOLDER_DOCS_UPLOAD')
      : t('CR_DESC_DEFAULT_PLACEHOLDER_MIGRATION'),
    isSiteUpload,
    externalAndUserSimpleMigration
  })

  const doClose = () => {
    onClose?.()
  }

  const handleSave = async (values: DocumentMigrationFormData) => {
    const clonedValues = { ...values }

    // Handle site upload folder type
    if (isSiteUpload && availableFolderTypes && linkedEntities) {
      const genericFolderType = availableFolderTypes.find(
        (ft) => ft.value === linkedEntities.entityLinkMap.GENERIC_ETMF_FOLDER_TYPE
      )
      if (genericFolderType) {
        clonedValues.folderTypeTemplateId = genericFolderType.id
      }
    }

    try {
      // Check for blinding status modal
      if (
        folderType?.allowCertifiedCopies &&
        selectedDocTypeObj?.allowBlindingStatusChange &&
        canEditBlinded &&
        isFolderWithinMedidataClinicalLineage
      ) {
        // Show blinding status modal, then certified copy modal
        showModal(
          <ConfirmationModal
            title={t('CONFIRM_BLINDING_STATUS_MODAL_TITLE')}
            message={t('CONFIRM_BLINDING_STATUS_MODAL_TITLE_QUESTION')}
            confirmText={t('YES')}
            cancelText={t('NO')}
            onConfirm={() => {
              clonedValues.isTriggerChangeBlindingStatus = true
              showCertifiedCopyModal(clonedValues)
            }}
            onCancel={() => {
              showCertifiedCopyModal(clonedValues)
            }}
          />,
          { keepParentModalsOpen: true }
        )
      } else if (
        selectedDocTypeObj?.allowBlindingStatusChange &&
        canEditBlinded &&
        isFolderWithinMedidataClinicalLineage
      ) {
        // Show blinding status modal, then perform migration
        showModal(
          <ConfirmationModal
            title={t('CONFIRM_BLINDING_STATUS_MODAL_TITLE')}
            message={t('CONFIRM_BLINDING_STATUS_MODAL_TITLE_QUESTION')}
            confirmText={t('YES')}
            cancelText={t('NO')}
            onConfirm={() => {
              clonedValues.isTriggerChangeBlindingStatus = true
              performMigration(clonedValues, false)
            }}
            onCancel={() => {
              performMigration(clonedValues, false)
            }}
          />,
          { keepParentModalsOpen: true }
        )
      } else if (folderType?.allowCertifiedCopies) {
        showCertifiedCopyModal(clonedValues)
      } else {
        await performMigration(clonedValues, false)
      }
    } catch (error) {
      console.error('Migration failed:', error)
    }
  }

  const showCertifiedCopyModal = (values: DocumentMigrationFormData) => {
    showModal(
      <ConfirmationModal
        title={t('MARK_AS_CERTIFIED_COPY_MODAL_TITLE')}
        message={t('MARK_AS_CERTIFIED_COPY_MODAL_TITLE_QUESTION')}
        confirmText={t('YES')}
        cancelText={t('NO')}
        onConfirm={() => {
          performMigration(values, true)
        }}
        onCancel={() => {
          performMigration(values, false)
        }}
      />,
      { keepParentModalsOpen: true }
    )
  }

  const performMigration = async (values: DocumentMigrationFormData, isCertifiedCopy: boolean) => {
    // Check if e-signature is required (matching legacy api.files-migration.js logic)
    const requireESignature =
      (values.createCR && context.service.auth.requireESignatureForFileCommands()) || isCertifiedCopy

    let eSignatureValidation = null

    if (requireESignature) {
      try {
        // Build e-signature message matching legacy implementation
        let esignMsg = ''
        if (context.service.auth.requireESignatureForFileCommands()) {
          esignMsg += t('DOCUMENT_MIGRATION_ESIGN_HEADER')
          if (values.createCR === true) {
            esignMsg += textFactory?.getText('DOCUMENT_MIGRATION')
          } else {
            esignMsg += textFactory?.getText('MIGRATIONS_WITOUT_CR')
          }
        }
        if (isCertifiedCopy) {
          esignMsg +=
            (context.service.auth.requireESignatureForFileCommands() ? '\n\n' : '') +
            t('CERT_COPY_MIGRATION_ESIGN_HEADER') +
            textFactory?.getText('CERTIFIED_COPY')
        }

        // Show e-signature modal and wait for validation
        const validation = await signAction('ADMIN_ACTION', esignMsg)

        // Extract only the fields needed by the backend API
        // For SSO/OKTA: backend expects only {relayState}, not the full validation with result
        // For DSO: backend expects only {username, password}
        if (validation.relayState) {
          // SSO/OKTA authentication - extract only relayState
          eSignatureValidation = { relayState: validation.relayState }
        } else {
          // DSO authentication - extract only username/password
          eSignatureValidation = {
            username: validation.username || '',
            password: validation.password || ''
          }
        }
      } catch (error) {
        // User cancelled e-signature or authentication failed
        console.log('E-signature cancelled by user')
        context.service.toast.error(t('ESIGNATURE_POPUP_SIGNED_WITH_DIFFERENT_ACC'))
        return
      }
    }

    // Initialize checkmate connection if checkmated
    if (context.service.checkmate.isCheckmated()) {
      try {
        await context.service.checkmate.initializeConnectionWithDirectRCM()
      } catch (error) {
        console.error('Failed to initialize checkmate connection:', error)
        context.service.toast.error(t('CHECKMATE_CONNECTION_ERROR'))
        return
      }
    }

    const crUsers: any = {}
    const filesToSend: FileWithMetadata[] = []
    let wasZipMigration = false

    for (const file of values.files) {
      filesToSend.push(file)
      const extension = file.name.split('.').pop()?.toLowerCase()
      if (extension === 'zip') {
        wasZipMigration = true
      }
    }

    if (values.createCR && values.typeData) {
      // Pass typeData directly to assembly functions (matching legacy behavior)
      // The assembly functions expect steps with `merged` containing objects like
      // {id: number, systemHelperType: 'USER'|'GROUP'} — this format is already
      // provided by both the API responses (USE_PREDEFINED) and the UserGroupSteps
      // component (SETUP_WITH_WORKFLOW). No manual transformation is needed.
      const assembledApprovers = context.api.files.assembleFsiApproversForServer(values.typeData)
      const assembledCollaborators = context.api.files.assembleFsiEditorsForServer(values.typeData)
      const assembledReviewers = context.api.files.assembleFsiReviewersForServer(values.typeData)

      // Always assign workflow steps arrays (matching legacy behavior)
      // Backend expects these arrays even if empty when createCR is true
      crUsers.approvers = assembledApprovers.approvers?.steps || []
      crUsers.collaborators = assembledCollaborators.collaborators?.steps || []
      crUsers.reviewers = assembledReviewers.reviewers?.steps || []
    }

    const migrationParams: any = {
      parentId: folderId || folderUuid,
      expirationDate:
        selectedDocTypeObj?.requireExpirationDate && values.expirationDate
          ? new Date(values.expirationDate).getTime()
          : null,
      documentTypeTemplateId: values.documentTypeTemplateId || undefined,
      description: values.description,
      isCertifiedCopy,
      continueToEditPhase: values.continueToEditPhase,
      createCR: values.createCR,
      isTriggerChangeBlindingStatus: values.isTriggerChangeBlindingStatus,
      combineImages: values.combineImages
    }

    // Only include folderTypeTemplateId if it has a value (matching legacy behavior)
    // Legacy code checks: if (params.folderTypeTemplateId) before adding to payload
    if (values.folderTypeTemplateId) {
      migrationParams.folderTypeTemplateId = values.folderTypeTemplateId
    }

    // Always include crUsers when createCR is true (matching legacy behavior)
    // Note: esignature is always set to 'null' in configureMigrationDetails when no validation provided
    if (values.createCR) {
      migrationParams.crUsers = crUsers
    }

    // Set up abort controller for DRCM disconnection
    const abortController = new AbortController()

    // Listen for disconnection from DRCM
    const handleDisconnect = () => {
      abortController.abort()
      clearInterval(sessionInterval)
    }
    window.addEventListener('disconnectedFromDRCM', handleDisconnect)

    // Prolong user session every 60 seconds during upload
    const prolongSession = () => {
      // Dispatch mouse event to keep session alive
      const event = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
        screenX: 0,
        screenY: 0
      })
      const element = document.elementFromPoint(0, 0)
      element?.dispatchEvent(event)
    }
    const sessionInterval = setInterval(prolongSession, 60000)

    try {
      // Close modal first to match legacy behavior
      // The uploader widget will be shown by uploaderService.startUpload()
      doClose()

      // Add small delay to ensure modal closes and DOM updates before upload starts
      // This allows the uploader widget to be properly displayed
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Call the migration API with progress callback and e-signature validation
      const result = await context.api.filesMigration.doMigration(
        filesToSend,
        migrationParams,
        (percentage: number) => {
          // Progress callback - could be used to show progress bar
          console.log(`Upload progress: ${percentage}%`)
        },
        undefined,
        undefined,
        abortController.signal,
        requireESignature,
        eSignatureValidation || undefined
      )

      // Check if migration was successful
      if (!result?.success) {
        // Migration failed - throw error to be caught by catch block
        throw new Error(result?.error || t('DOCUMENT_MIGRATION_ERROR'))
      }

      // Show success message
      if (values.createCR) {
        context.service.toast.success(t('DOCUMENT_MIGRATION_CR_SUCCESS_MSG'))
      } else if (isSiteUpload) {
        context.service.toast.success(t('DOCUMENT_MIGRATION_SITE_UPLOAD_SUCCESS_MSG'))
      } else if (wasZipMigration) {
        context.service.toast.success(t('DOCUMENT_MIGRATION_ZIP_SUCCESS_MSG'))
      } else {
        context.service.toast.success(t('DOCUMENT_MIGRATION_SUCCESS_MSG'))
      }

      // Emit event to update tables (legacy compatibility)
      window.dispatchEvent(new CustomEvent('updateTables', { detail: true }))
      window.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['files'] })
      queryClient.invalidateQueries({ queryKey: ['workflows'] })

      // Call onSuccess callback if provided
      onSuccess?.()
    } catch (error: any) {
      // Display error message
      context.service.toast.error(error?.message || t('DOCUMENT_MIGRATION_ERROR'))
    } finally {
      // Cleanup
      clearInterval(sessionInterval)
      window.removeEventListener('disconnectedFromDRCM', handleDisconnect)
    }
  }

  const handleDocTypeChange = async (selectedDocType: { value: number; label: string } | null) => {
    if (!selectedDocType?.value) {
      // Clear document type if no selection
      if (formikRef.current) {
        formikRef.current.setFieldValue('documentTypeTemplateId', null)
        formikRef.current.setFieldValue('selectedDocTypeObj', null)
      }
      setFormData((prev: DocumentMigrationFormData) => ({
        ...prev,
        documentTypeTemplateId: null,
        files: formikRef.current?.values.files ?? prev.files
      }))
      setSelectedDocTypeObj(null)
      return
    }

    try {
      const data = await getById(selectedDocType.value, false)

      const selectedDocTypeObjData = data as any

      // Check if metadata templates are available (matching legacy implementation)
      const isOptionalMetadataTempAvailable =
        selectedDocTypeObjData?.optionalMetadataTemplates &&
        Array.isArray(selectedDocTypeObjData.optionalMetadataTemplates) &&
        selectedDocTypeObjData.optionalMetadataTemplates.length !== 0

      const isRequiredMetadataTempAvailable =
        selectedDocTypeObjData?.requiredMetadataTemplates &&
        Array.isArray(selectedDocTypeObjData.requiredMetadataTemplates) &&
        selectedDocTypeObjData.requiredMetadataTemplates.length !== 0

      if (formikRef.current) {
        // Set documentTypeTemplateId at root level (this is what validation checks)
        formikRef.current.setFieldValue('documentTypeTemplateId', selectedDocType.value)

        // Set selectedDocTypeObj at root level for reference
        formikRef.current.setFieldValue('selectedDocTypeObj', selectedDocTypeObjData)

        // For external uploads, enable CR and set continueToEditPhase based on metadata templates
        if (externalAndUserSimpleMigration) {
          formikRef.current.setFieldValue('createCR', true)
          formikRef.current.setFieldValue(
            'continueToEditPhase',
            isOptionalMetadataTempAvailable || isRequiredMetadataTempAvailable
          )
        }

        // Sync formData state so Formik reinitialization (enableReinitialize)
        // preserves all values instead of resetting them to stale initialValues
        setFormData((prev: DocumentMigrationFormData) => ({
          ...prev,
          documentTypeTemplateId: selectedDocType.value,
          files: formikRef.current?.values.files ?? prev.files,
          ...(externalAndUserSimpleMigration && {
            createCR: true,
            continueToEditPhase: isOptionalMetadataTempAvailable || isRequiredMetadataTempAvailable
          })
        }))
      }
      // Note: continueToEditPhase for regular migrations is NOT auto-set based on metadata templates
      // in the legacy code. User must manually check the checkbox if desired.

      setSelectedDocTypeObj(selectedDocTypeObjData as DocumentTypeOption)
      await downloadAllTaskGroupSteps(selectedDocTypeObjData as DocumentTypeOption)
    } catch (error) {
      console.error('Error loading document type:', error)
      // Clear the document type on error
      if (formikRef.current) {
        formikRef.current.setFieldValue('documentTypeTemplateId', null)
        formikRef.current.setFieldValue('selectedDocTypeObj', null)
      }
      setFormData((prev: DocumentMigrationFormData) => ({
        ...prev,
        documentTypeTemplateId: null,
        files: formikRef.current?.values.files ?? prev.files
      }))
      setSelectedDocTypeObj(null)
      context.service.toast.error(t('ERROR_LOADING_DOCUMENT_TYPE'))
    }
  }

  const downloadAllTaskGroupSteps = async (selectedDocTypeObjData: DocumentTypeOption) => {
    const typeData: any = {
      approvers: { steps: [] },
      reviewers: { steps: [] },
      collaborators: { steps: [] }
    }

    if (selectedDocTypeObjData?.workflowTemplate?.id) {
      try {
        const data = await changeRequestTypesApiService.get(selectedDocTypeObjData.workflowTemplate.id)
        const fetchedTypeData = await changeRequestTypesApiService.assembleForUI(data, true)
        // Map fetched data to typeData structure
        if (fetchedTypeData.approvers) typeData.approvers = fetchedTypeData.approvers
        if (fetchedTypeData.reviewers) typeData.reviewers = fetchedTypeData.reviewers
        if (fetchedTypeData.collaborators) typeData.collaborators = fetchedTypeData.collaborators

        if (formikRef.current) {
          formikRef.current.setFieldValue('typeData', typeData)
        }
      } catch (error) {
        console.error('Error loading workflow template:', error)
        doClose()
      }
    } else {
      const parentId = folderId || folderUuid
      if (!parentId) return

      try {
        const [approvers, collaborators, reviewers] = await Promise.all([
          context.api.files.getFileApprovers(Number(parentId)),
          context.api.files.getFsiEditors(Number(parentId), true),
          context.api.files.getFileReviewers(Number(parentId))
        ])

        // Preserve the entire response objects to keep the 'type' property for conditional rendering
        typeData.approvers = approvers.approvers
        typeData.collaborators = collaborators.collaborators
        typeData.reviewers = reviewers.reviewers

        if (formikRef.current) {
          formikRef.current.setFieldValue('typeData', typeData)
        }
      } catch (error) {
        console.error('Error loading task groups:', error)
      }
    }
  }

  const checkIsFolderWithinMedidataClinicalLineage = (pathArr: any[]): boolean => {
    return pathArr[1]?.name === 'Clinical' || pathArr[1]?.name === 'Medidata'
  }

  const getFolderTypes = async (folderTypeData?: any) => {
    try {
      const data = (await context.api.folderTypes.getAllFolderTypes({ sortBy: 'name', sortDesc: false })) as any
      const transformedOptions = data.elements.map((ft: any) => ({
        id: ft.id,
        name: ft.name,
        value: ft.id,
        label: ft.name,
        ...ft
      }))

      setAvailableFolderTypes(transformedOptions)

      let newFolderTypeId: string | number | null = null
      if (folderTypeData) {
        // Find the parent folder type in the transformed options
        const parentFolderType = transformedOptions.find((ft: FolderTypeOption) => ft.id === folderTypeData.id)
        if (parentFolderType) {
          newFolderTypeId = parentFolderType.id
        }
      } else if (transformedOptions.length > 0) {
        // No folderTypeData provided, set default to first available
        newFolderTypeId = transformedOptions[0].id
      }

      if (newFolderTypeId !== null) {
        setFormData((prev: DocumentMigrationFormData) => ({
          ...prev,
          folderTypeTemplateId: newFolderTypeId,
          files: formikRef.current?.values.files ?? prev.files
        }))
        if (formikRef.current) {
          formikRef.current.setFieldValue('folderTypeTemplateId', newFolderTypeId)
        }
      }
    } catch (error) {
      console.error('Error loading folder types:', error)
    }
  }

  const assembleAllowedDocTypes = async (folderTypeData: any) => {
    try {
      const data = await context.api.etmf.getLinkedEntities()
      setLinkedEntities(data)

      if (isSiteUpload) {
        setAllowedDocTypes([Number(data?.entityLinkMap.GENERIC_ETMF_DOC_TYPE)])
        setFormData((prev: DocumentMigrationFormData) => ({
          ...prev,
          documentTypeTemplateId: Number(data?.entityLinkMap.GENERIC_ETMF_DOC_TYPE),
          files: formikRef.current?.values.files ?? prev.files
        }))
      } else if (folderTypeData.documentTypesAllowed !== 'ALL') {
        setAllowedDocTypes(folderTypeData.allowedDocumentTypeTemplateIds)
        setFormData((prev: DocumentMigrationFormData) => ({
          ...prev,
          documentTypeTemplateId: folderTypeData.allowedDocumentTypeTemplateIds?.[0] || null,
          files: formikRef.current?.values.files ?? prev.files
        }))
      } else {
        // When 'ALL' document types are allowed, set to null to show all available types
        setAllowedDocTypes(null)
      }
    } catch (error) {
      console.error('Failed to fetch linked entities:', error)
    }
  }

  useEffect(() => {
    const init = async () => {
      const parentId = folderId || folderUuid
      if (!parentId) return

      try {
        const parentIdNum = typeof parentId === 'number' ? parentId : Number(parentId)
        const [fileDetails, fsiPath] = await Promise.all([
          context.api.files.getDetails(parentIdNum),
          context.api.files.getFsiPath(parentIdNum)
        ])

        setCanEditBlinded(fileDetails.actionFlags.canEditBlinded)
        setIsFolderWithinMedidataClinicalLineage(checkIsFolderWithinMedidataClinicalLineage(fsiPath))

        // Optimization: Only fetch folder type if it's a REGULATED_CONTENT folder or has templateId
        // This matches legacy behavior and avoids unnecessary API calls
        if (params?.parentFolderType === 'REGULATED_CONTENT' || params?.templateId) {
          const folderTypeData = await context.api.files.getFolderFolderType(parentIdNum)
          if (folderTypeData) {
            setFolderType(folderTypeData)
            await assembleAllowedDocTypes(folderTypeData)
            await getFolderTypes(folderTypeData)
          } else {
            await assembleAllowedDocTypes({ documentTypesAllowed: FOLDER_TYPE_DOCUMENT_TYPES_ALLOWED.ALL })
            await getFolderTypes()
          }
        } else {
          // For non-regulated folders, use default 'ALL' document types
          await assembleAllowedDocTypes({ documentTypesAllowed: FOLDER_TYPE_DOCUMENT_TYPES_ALLOWED.ALL })
          await getFolderTypes()
        }
      } catch (error) {
        console.error('Error initializing modal:', error)
      }
    }

    init()
  }, [])

  const [show, setShow] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.body.classList.add('modal-open')
    // Delayed show for animation
    const timer = setTimeout(() => setShow(true), 10)
    return () => {
      clearTimeout(timer)
      document.body.classList.remove('modal-open')
    }
  }, [])

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Static backdrop - don't close on backdrop click
    if (e.target === e.currentTarget) {
      e.stopPropagation()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      doClose()
    }
  }

  if (!show) return null

  return (
    <>
      <Modal
        show={isOpenProp}
        handleClose={doClose}
        title={
          externalAndUserSimpleMigration || isSiteUpload
            ? t('DOC_MIGRATION_H_TITLE_DOC_UPLOAD')
            : t('DOCUMENT_MIGRATION_TITLE')
        }
        size="lg"
        className="theme-sandman docsMigrationPage"
      >
        <Formik
          innerRef={formikRef}
          initialValues={formData}
          enableReinitialize={true}
          validate={(values) => {
            try {
              // Pass values into context so schema can see $createCR
              validationSchema.validateSync(values, {
                abortEarly: false,
                context: values
              })
              return {}
            } catch (err) {
              return yupToFormErrors(err)
            }
          }}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={handleSave}
        >
          {({ setFieldValue, values, handleSubmit, errors, setTouched }) => (
            <>
              <Modal.Body className="docsMigrationPage">
                <FileDropZone
                  isSiteUpload={isSiteUpload}
                  selectedFiles={values.files}
                  setSelectedFiles={(files) => {
                    setFieldValue('files', files, true)
                  }}
                />
                {errors.files && <div className="text-danger">{t(String(errors.files))}</div>}

                <div className="form-group w50p wparag mt15">
                  <label>{t('DOC_MIGRATION_DOC_TYPE_LABEL')}</label>
                  <DocTypeDropdown
                    placeholder="SELECT_DOC_TYPE_PLACEHOLDER"
                    name="documentTypeTemplateId"
                    isSingle={true}
                    required={true}
                    fromDocMig={true}
                    {...(allowedDocTypes ? { allowedTypes: allowedDocTypes } : {})}
                    onChange={(option: any) => {
                      if (option && !Array.isArray(option)) {
                        handleDocTypeChange(option)
                      }
                    }}
                  />
                  {errors.documentTypeTemplateId && (
                    <div className="text-danger">{t(errors.documentTypeTemplateId as string)}</div>
                  )}
                </div>

                {!externalAndUserSimpleMigration && !isSiteUpload && (
                  <div className="form-group w50p">
                    <label>{t('DOC_MIGRATION_FOLDER_TYPE_LABEL')}</label>
                    <Select
                      value={values.folderTypeTemplateId}
                      isClearable={false}
                      placeholder={t('SELECT_FOLDER_TYPE_PLACEHOLDER')}
                      name="folderTypeTemplateId"
                      options={availableFolderTypes?.map((ft) => ({ value: ft.id || '', label: ft.name || '' })) || []}
                      onChange={(option: any) => {
                        if (option && !Array.isArray(option)) {
                          setFieldValue('folderTypeTemplateId', option.value)
                        }
                      }}
                    />
                    {errors.folderTypeTemplateId && (
                      <div className="text-danger">{t(String(errors.folderTypeTemplateId))}</div>
                    )}
                  </div>
                )}

                {!externalAndUserSimpleMigration && !isSiteUpload && (
                  <div className="form-group">
                    <label className="checkbox-label-dm">
                      <input
                        type="checkbox"
                        checked={values.createCR}
                        onChange={(e) => setFieldValue('createCR', e.target.checked)}
                      />
                      <span>{t('DOC_MIGRATION_CREATE_CR_LABEL')}</span>
                    </label>
                  </div>
                )}

                {(externalAndUserSimpleMigration || values.createCR) && (
                  <div className="form-group">
                    <label className="checkbox-label-dm">
                      <input
                        type="checkbox"
                        checked={values?.continueToEditPhase}
                        onChange={(e) => setFieldValue('continueToEditPhase', e.target.checked)}
                      />
                      <span>{t('DOC_MIGRATION_CONTINUE_EDITING_LABEL')}</span>
                    </label>
                  </div>
                )}

                {selectedDocTypeObj?.requireExpirationDate && (
                  <div className="form-group w50p">
                    <label>{t('BROWSER_ADD_DOCUMENT_EXPIRATION_DATE_LABEL')}</label>
                    <FormGroup>
                      <DatePicker
                        placeholderText={t('-SELECT_DATE-')}
                        name="expirationDate"
                        onChange={(val: any) => {
                          setFieldValue('expirationDate', val)
                        }}
                        popperProps={{ positionFixed: true }}
                        autoComplete="off"
                        selected={values?.expirationDate}
                        popoverTargetOffset="3px 0"
                        todayButton="Today"
                        dateFormatCalendar="MMMM"
                        dateFormat={getDatePickerParseFormats()}
                        showYearDropdown
                        className="form-control field-control w100p"
                        id="2f5f49a5-cba9-47c7-96cb-d7b301c530a6"
                      />
                      {errors.expirationDate && <div className="text-danger">{t(String(errors.expirationDate))}</div>}
                    </FormGroup>
                  </div>
                )}

                {values?.createCR && (
                  <div>
                    <div className="form-group w50p mt10">
                      <label>{t('DESCRIPTION')}</label>
                      <TextArea
                        className="w100p"
                        maxLength={254}
                        name="description"
                        onChange={(event: any) => {
                          const { value } = event.target
                          setFieldValue('description', value)
                        }}
                        id="qc-desc"
                        value={values?.description}
                        rows={4}
                      />
                      {errors.description && <div className="text-danger">{t(errors.description)}</div>}
                    </div>

                    {(values.typeData?.collaborators as any)?.type === 'SETUP_WITH_WORKFLOW' && (
                      <>
                        <FieldArray name="typeData.collaborators.steps">
                          {(arrayHelpers: any) => (
                            <UserGroupSteps
                              arrayHelpers={arrayHelpers}
                              values={values?.typeData?.collaborators?.steps || []}
                              nofallTxt="BROWSER_FILE_SETTINGS_EDITORS_INFO_LABEL"
                              labelTxt="BROWSER_FILE_SETTINGS_EDITORS_SEQUENCE_HEADLINE"
                              increaseUserGroupWidth={true}
                              autoAddUser={true}
                            />
                          )}
                        </FieldArray>
                      </>
                    )}

                    {(values.typeData?.reviewers as any)?.type === 'SETUP_WITH_WORKFLOW' && (
                      <>
                        <FieldArray name="typeData.reviewers.steps">
                          {(arrayHelpers: any) => (
                            <UserGroupSteps
                              arrayHelpers={arrayHelpers}
                              values={values?.typeData?.reviewers?.steps || []}
                              nofallTxt="BROWSER_FILE_SETTINGS_REVIEWERS_INFO_LABEL"
                              labelTxt="BROWSER_FILE_SETTINGS_REVIEWERS_REVIEW_SEQUENCE_HEADLINE"
                              increaseUserGroupWidth={true}
                            />
                          )}
                        </FieldArray>
                      </>
                    )}

                    {(values.typeData?.approvers as any)?.type === 'SETUP_WITH_WORKFLOW' && (
                      <>
                        <FieldArray name="typeData.approvers.steps">
                          {(arrayHelpers: any) => (
                            <UserGroupSteps
                              arrayHelpers={arrayHelpers}
                              values={values?.typeData?.approvers?.steps || []}
                              nofallTxt="BROWSER_FILE_SETTINGS_APPROVERS_INFO_LABEL"
                              labelTxt="BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                              increaseUserGroupWidth={true}
                            />
                          )}
                        </FieldArray>
                      </>
                    )}
                  </div>
                )}
              </Modal.Body>

              <Modal.Footer>
                <button className="btn btn-close btn-link" type="button" onClick={doClose}>
                  {t('CANCEL')}
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={async () => {
                    // Mark all fields as touched to show validation errors
                    setTouched({
                      documentTypeTemplateId: true,
                      folderTypeTemplateId: true,
                      expirationDate: true,
                      description: true
                    } as any)
                    // Trigger form submission
                    const result = handleSubmit()
                  }}
                >
                  {externalAndUserSimpleMigration || isSiteUpload
                    ? t('UPLOAD')
                    : t('DOCUMENT_MIGRATION_START_MIGRATION')}
                </button>
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>
      {/* E-Signature Modal (for DSO/RCM authentication) */}
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </>
  )
}

export default DocumentMigrationModal
